<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useDark } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import IconNotification from '@/assets/svg/icon-bell-notification.svg'
import IconInfo from '@/assets/svg/IconInfo.svg'
import IconPhone from '@/assets/svg/icon-phone.svg'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { getAppTimeZone } from '@/helpers/app-timezone-datetime.helper'
import { useAuthStore } from '@/stores/useAuthStore'
import { useNotificationStore } from '@/stores/useNotificationStore'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { t, locale } = useI18n()
const { shouldBlockWebphone, checkPermissions, tooltipKey } = useWebphonePermissions()
const isDark = useDark({ selector: 'html' })
const isScrolled = ref(false)
const isNotificationsOpen = ref(false)
const notificationsRef = ref<HTMLElement | null>(null)

const extensionNumber = computed(() => authStore.extension?.number || 'N/A')
const extensionDescription = computed(() => authStore.extension?.description || '-')
const isExtensionUnavailable = computed(() => !authStore.webphoneAvailable)
const isWebphoneDisabled = computed(() => shouldBlockWebphone.value || isExtensionUnavailable.value)
const webphoneTooltip = computed(() => t(tooltipKey.value))
const extensionStatusLabel = computed(() =>
  locale.value === 'en'
    ? isExtensionUnavailable.value
      ? 'Disconnected'
      : 'Connected'
    : isExtensionUnavailable.value
      ? 'Desconectada'
      : 'Conectada',
)
// const extensionNameWithStatus = computed(
//   () => `${extensionDescription.value} - ${extensionStatusLabel.value}`,
// )
const extensionNameWithStatus = computed(() => `${extensionDescription.value}`)
const currentDate = computed(() => {
  const currentLocale = locale.value === 'en' ? 'en-US' : 'es-ES'

  return new Intl.DateTimeFormat(currentLocale, {
    timeZone: getAppTimeZone(),
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date())
})

const notifications = computed(() =>
  notificationStore.notifications.filter((notification) => notification.type === 'incoming-call'),
)
const totalNotifications = computed(() => notifications.value.length)
const isIncomingCallRinging = computed(() => authStore.webphoneCallStatus === 'incoming')

const formatNotificationDate = (dateIso: string) => {
  const currentLocale = locale.value === 'en' ? 'en-US' : 'es-ES'
  const date = new Date(dateIso)
  return new Intl.DateTimeFormat(currentLocale, {
    timeZone: getAppTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 8
}

const toggleNotifications = () => {
  isNotificationsOpen.value = !isNotificationsOpen.value
}

const refreshWebphonePermissions = () => {
  void checkPermissions()
}

const handleWebphoneClick = () => {
  if (shouldBlockWebphone.value) return

  authStore.toggleWebphone()
}

const handleDocumentClick = (event: MouseEvent) => {
  if (!isNotificationsOpen.value) return

  const target = event.target as Node | null
  if (!target) return

  if (notificationsRef.value?.contains(target)) return
  isNotificationsOpen.value = false
}

onMounted(() => {
  refreshWebphonePermissions()
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('focus', refreshWebphonePermissions)
  document.addEventListener('visibilitychange', refreshWebphonePermissions)
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('focus', refreshWebphonePermissions)
  document.removeEventListener('visibilitychange', refreshWebphonePermissions)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <header
    class="app-glass m-3 px-4 py-3 justify-between hidden border border-white/70 dark:border-white/10 lg:flex flex-row rounded-lg !shadow-xs transition-shadow duration-150 relative"
    :class="[
      isNotificationsOpen ? 'z-[90]' : 'z-30',
      isDark ? 'app-glass--dark' : '',
      isScrolled
        ? 'shadow-sm border-slate-200/80 dark:border-white/15'
        : 'border-white/70 dark:border-white/10',
    ]"
  >
    <div class="flex items-center gap-3 pl-2">
      <div class="flex items-center gap-2">
        <div
          @click="handleWebphoneClick"
          class="relative transition-transform duration-150 active:scale-95"
          :class="isWebphoneDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-105'"
        >
          <IconPhone
            class="w-11 h-9"
            :class="[
              isWebphoneDisabled ? 'text-slate-400' : 'text-(--p-primary-color)',
              !isWebphoneDisabled && isIncomingCallRinging ? 'phone-ringing' : '',
            ]"
          />
          <span
            class="absolute inset-0 flex items-center ml-4.5 justify-center text-[10px] font-medium leading-none ibrand"
            :class="isWebphoneDisabled ? 'text-slate-400' : 'text-(--p-primary-color)'"
          >
            {{ extensionNumber }}
          </span>
        </div>

        <button
          v-if="shouldBlockWebphone"
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full text-amber-500 transition hover:bg-amber-50 dark:hover:bg-amber-400/10 cursor-pointer"
          v-tooltip.bottom="webphoneTooltip"
          @mouseenter="refreshWebphonePermissions"
          @focus="refreshWebphonePermissions"
        >
          <IconInfo class="h-5 w-5" />
        </button>
      </div>

      <div class="ml-2 flex flex-col leading-tight gap-0.5">
        <div class="flex items-center gap-2">
          <span class="quicksand font-semibold text-sm text-slate-800 dark:text-slate-100">
            {{ extensionNameWithStatus }}
          </span>
          <span
            class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
            :class="
              isExtensionUnavailable
                ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/40'
            "
          >
            <span
              class="h-1.5 w-1.5 rounded-full"
              :class="isExtensionUnavailable ? 'bg-slate-400' : 'bg-emerald-500'"
            />
            {{ extensionStatusLabel }}
          </span>
        </div>
        <span class="montserrat text-xs text-slate-500 dark:text-slate-400 capitalize">
          {{ currentDate }}
        </span>
      </div>
    </div>

    <div ref="notificationsRef" class="relative flex items-center gap-2 mr-1 z-50">
      <button
        type="button"
        @click="toggleNotifications"
        class="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/70 bg-white/70 dark:border-white/10 dark:bg-white/5 text-slate-600 dark:text-slate-300 transition-colors duration-150 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-(--p-primary-color) dark:hover:text-white cursor-pointer relative"
        aria-label="Notifications"
      >
        <IconNotification width="17" class="text-slate-600 dark:text-slate-300" />
        <span
          v-if="totalNotifications > 0"
          class="absolute -top-1 -right-1 min-w-4.5 h-4.5 rounded-full bg-(--p-primary-color) text-white text-[10px] font-semibold flex items-center justify-center px-1 shadow-xs"
        >
          {{ totalNotifications > 99 ? '99+' : totalNotifications }}
        </span>
      </button>

      <div
        v-if="isNotificationsOpen"
        class="absolute right-0 top-11 z-[100] w-96 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200/90 bg-white/[0.98] backdrop-blur-xl shadow-xl shadow-slate-900/10 dark:border-white/12 dark:bg-[#131620]/[0.98] dark:shadow-2xl dark:shadow-black/50 overflow-hidden text-slate-800 dark:text-slate-100"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-slate-200/70 dark:border-white/10 bg-slate-50/80 dark:bg-white/[0.04]"
        >
          <span class="text-sm font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
            {{ t('common.general.notifications') }}
          </span>
          <button
            v-if="notifications.length > 0"
            type="button"
            class="text-xs font-medium text-(--p-primary-color) hover:text-(--p-primary-600) dark:text-(--p-primary-400) dark:hover:text-(--p-primary-300) transition-colors cursor-pointer"
            @click="notificationStore.clearNotifications()"
          >
            {{ t('common.actions.clear') }}
          </button>
        </div>

        <div v-if="notifications.length === 0" class="px-4 py-8 text-sm text-slate-500 dark:text-slate-400 text-center font-medium">
          {{ t('common.general.no_results') }}
        </div>

        <div v-else class="max-h-96 overflow-y-auto divide-y divide-slate-100/80 dark:divide-white/6">
          <div
            v-for="item in notifications"
            :key="item.id"
            class="px-4 py-3.5 transition-colors hover:bg-slate-50/80 dark:hover:bg-white/[0.03]"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <p class="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">{{ item.title }}</p>
                <p class="text-xs text-slate-600 dark:text-slate-300/90 leading-relaxed break-words mt-0.5">{{ item.message }}</p>
                <p
                  v-if="item.incomingCallDecision"
                  class="text-[11px] mt-1 font-medium"
                  :class="
                    item.incomingCallDecision === 'accepted'
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-rose-700 dark:text-rose-400'
                  "
                >
                  {{ item.incomingCallDecision === 'accepted' ? 'Aceptada' : 'Rechazada' }}
                </p>
                <p class="text-[11px] text-slate-400 dark:text-slate-500 mt-1 font-mono">
                  {{ formatNotificationDate(item.createdAt) }}
                </p>
              </div>
              <button
                type="button"
                class="text-xs font-medium text-slate-400 hover:text-rose-600 dark:text-slate-500 dark:hover:text-rose-400 transition-colors p-1 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/30 cursor-pointer shrink-0"
                @click="notificationStore.removeNotification(item.id)"
              >
                {{ t('common.actions.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
@keyframes phone-ring {
  0% {
    transform: rotate(0deg);
  }
  15% {
    transform: rotate(-10deg);
  }
  30% {
    transform: rotate(10deg);
  }
  45% {
    transform: rotate(-10deg);
  }
  60% {
    transform: rotate(10deg);
  }
  75% {
    transform: rotate(-4deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.phone-ringing {
  transform-origin: 50% 85%;
  animation: phone-ring 0.8s ease-in-out infinite;
}
</style>
